import FF from "@/components/forms/FormField.module.css";

import { activateMutation, deactivateMutation, suspensionReasonMutation } from "@/admin/accounts/accountsQuery.ts";
import type { GQAccountItem } from "@/admin/accounts/AccountsTable.tsx";
import { BillingAccountBlock } from "@/admin/accounts/BillingAccountBlock.tsx";
import { SuspensionReasonTextBlock } from "@/admin/accounts/SuspensionReasonTextBlock.tsx";
import { Badge } from "@/components/Badge.tsx";
import { EnumBadge, type EnumProps } from "@/components/EnumBadge.tsx";
import { EditableButton } from "@/components/forms/EditableButton.tsx";
import { WRadio } from "@/components/forms/WRadio.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { cn } from "@/utils/classNames.ts";
import type { BillingAccountStatus } from "@warrenio/api-spec/spec.oats.gen";
import { useState } from "react";
import { RadioGroup } from "react-aria-components";
import { entries } from "remeda";

interface StatusBadgeModalProps {
    item: GQAccountItem;
    value: BillingAccountStatus | undefined;
}

export function StatusBadge({ value }: { value: BillingAccountStatus | undefined }) {
    if (!value) return null;
    return <EnumBadge options={statusDescriptions} value={value} />;
}

export function StatusBadgeButton(props: StatusBadgeModalProps) {
    const { value } = props;

    return (
        <WModal
            button={
                <EditableButton action={undefined}>
                    <StatusBadge value={value} />
                </EditableButton>
            }
        >
            <StatusBadgeModalContent {...props} />
        </WModal>
    );
}

function StatusBadgeModalContent({ item }: StatusBadgeModalProps) {
    const activateBaMutation = useStandardMutation(activateMutation);
    const deactivateBaMutation = useStandardMutation(deactivateMutation);
    const modifyMutation = useStandardMutation(suspensionReasonMutation);
    const { id } = item;
    const [reason, setReason] = useState<string>(item.suspend_reason ?? "");
    const [status, setStatus] = useState<BillingAccountStatus>(item.status as BillingAccountStatus);

    async function onSubmit() {
        if (status === "SUSPENDED") {
            await deactivateBaMutation.mutateAsync({
                body: {
                    billing_account_id: Number(id),
                },
            });

            if (reason) {
                await modifyMutation.mutateAsync({
                    body: {
                        billing_account_id: Number(id),
                        reason: reason ?? "",
                    },
                });
            }
        } else {
            await activateBaMutation.mutateAsync({
                body: {
                    billing_account_id: Number(id),
                },
            });
        }
    }

    const radios = entries(statusDescriptions).map(([key, type]) => {
        const isSelected = status === key;

        return (
            <WRadio
                className="grid-col-span-2"
                key={key}
                value={key}
                badge={
                    <Badge reverse={isSelected} noDot color={type.color}>
                        {type.label}
                    </Badge>
                }
                isDisabled={key === "PENDING_VALIDATION"}
                description={type.description}
            />
        );
    });

    return (
        <WModalContent title="Change billing account status" label="Change" modalAction={async () => await onSubmit()}>
            <div className="flex gap-3 items-center justify-between lightBlock">
                <MaskIcon className="jp-card-icon color-primary size-2.5rem" />
                <div className="flex-grow VStack gap-0.5">
                    <BillingAccountBlock showCompanyName isLink={false} value={item} />
                </div>
            </div>

            <div className="pt-3 pb-2">Change status*</div>
            <RadioGroup
                aria-label="Select status"
                className={cn(FF.FormFieldRadioGroup, "grid-auto-flow-col")}
                value={status}
                onChange={(v) => setStatus(v as BillingAccountStatus)}
            >
                {radios}
            </RadioGroup>

            {status === "SUSPENDED" && <SuspensionReasonTextBlock value={reason} onChange={setReason} />}
        </WModalContent>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export const statusDescriptions: Record<BillingAccountStatus, EnumProps> = {
    PENDING_VALIDATION: {
        label: "Pending validation",
        description: "Billing account is waiting to be validated before it can be used.",
        color: "warning",
    },
    ACTIVE: {
        label: "Active",
        description: "Billing account is usable, user can add payment methods or top up the account.",
        color: "success",
    },
    SUSPENDED: {
        label: "Suspended",
        description:
            "Billing account is not usable, all related resources will be stopped, user will not be able to start them again. User can not create new billing accounts if they have a suspended account, even if the account is deleted.",
        color: "warning",
    },
};
